import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function Landing() {
  const { loginWithPassword, register } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  const goToAuth = (m: 'login' | 'register') => {
    setMode(m);
    setError(null);
    document.getElementById('auth')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'register') {
        await register({
          email: email.trim(),
          password,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        });
      } else {
        await loginWithPassword(email.trim(), password);
      }
      // On success the AuthProvider sets the user and the app re-renders away
      // from the landing page automatically.
    } catch (err: unknown) {
      const e2 = err as { response?: { data?: { error?: string } } };
      setError(e2.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">Trapp</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => goToAuth('login')}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary rounded-lg transition-colors"
            >
              Log In
            </button>
            <button
              onClick={() => goToAuth('register')}
              className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              Register
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-6">
          Combat Wildlife Poaching with{' '}
          <span className="text-primary">Offline-First</span> Snare Logging
        </h2>
        <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
          Trapp is a free mobile application for conservation teams to log snare and carcass discoveries
          in the field, even without internet connectivity. Sync your data when you're back online.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button
            onClick={() => goToAuth('register')}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-bold text-lg hover:opacity-90 transition-opacity shadow-lg"
          >
            Get Started
          </button>
          <a
            href="#how-it-works"
            className="px-8 py-4 bg-white border-2 border-border text-foreground rounded-xl font-bold text-lg hover:bg-secondary transition-colors"
          >
            Learn More
          </a>
        </div>
      </section>

      {/* Auth Section */}
      <section id="auth" className="max-w-md mx-auto px-4 pb-16 w-full">
        <div className="bg-white rounded-2xl p-8 border border-border shadow-lg">
          <div className="flex rounded-lg bg-secondary p-1 mb-6">
            <button
              type="button"
              onClick={() => { setMode('login'); setError(null); }}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${mode === 'login' ? 'bg-white shadow-sm' : 'text-muted-foreground'}`}
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => { setMode('register'); setError(null); }}
              className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${mode === 'register' ? 'bg-white shadow-sm' : 'text-muted-foreground'}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-input rounded-lg"
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-input rounded-lg"
                  autoComplete="family-name"
                />
              </div>
            )}
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-input rounded-lg"
              autoComplete="email"
            />
            <input
              type="password"
              required
              minLength={8}
              placeholder={mode === 'register' ? 'Password (min 8 characters)' : 'Password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-input rounded-lg"
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            />

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive rounded-lg text-sm text-destructive">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting ? 'Please wait…' : mode === 'register' ? 'Create account' : 'Log in'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">OR</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            className="w-full py-3 bg-white border-2 border-border text-foreground rounded-lg font-semibold hover:bg-secondary transition-colors"
          >
            Continue with Google
          </button>

          <p className="text-xs text-muted-foreground text-center mt-4">
            After registering, request to join your conservation group — a group admin will approve your access.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="max-w-6xl mx-auto px-4 py-16" id="how-it-works">
        <h3 className="text-3xl font-bold text-center mb-12">How Trapp Works</h3>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <h4 className="text-xl font-bold mb-3">1. Register & Join</h4>
            <p className="text-muted-foreground">
              Create your free account and request to join an existing conservation group. Group admins will
              approve your access to start logging data.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <h4 className="text-xl font-bold mb-3">2. Log in the Field</h4>
            <p className="text-muted-foreground">
              Record snare and carcass discoveries with GPS coordinates, photos, and notes—even when offline.
              All data is stored locally until you have connectivity.
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </div>
            <h4 className="text-xl font-bold mb-3">3. Sync & Analyze</h4>
            <p className="text-muted-foreground">
              When back online, your data automatically syncs to the cloud. View maps, generate reports,
              and coordinate anti-poaching efforts with your team.
            </p>
          </div>
        </div>
      </section>

      {/* Create Group Section */}
      <section className="bg-primary/5 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h3 className="text-3xl font-bold mb-6">Want to Create a Group?</h3>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            If you're leading a conservation organization or anti-poaching team and would like to
            create a group on Trapp, we'd love to help you get set up.
          </p>
          <div className="bg-white rounded-xl p-8 border border-border shadow-sm">
            <h4 className="text-xl font-semibold mb-4">Getting Started</h4>
            <ol className="text-left space-y-3 mb-6">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </span>
                <span>Register for a free Trapp account</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </span>
                <span>Email us at <strong>trapp@xeroth.ai</strong> with details about your organization</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </span>
                <span>We'll set up your group and grant you admin access</span>
              </li>
            </ol>
            <a
              href="mailto:trapp@xeroth.ai?subject=Request to Create Trapp Group"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-border">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="6" />
                  <circle cx="12" cy="12" r="2" />
                </svg>
              </div>
              <span className="text-xl font-bold">Trapp</span>
            </div>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Trapp is a <strong>free service</strong> provided by{' '}
              <a href="https://xeroth.ai" className="text-primary hover:underline font-semibold" target="_blank" rel="noopener noreferrer">
                Xeroth AI
              </a>{' '}
              to help conservation teams eliminate the scourge of wildlife poaching.
            </p>
            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} Xeroth AI. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
